import React, { useState } from "react";
import axios from "axios";
import { BASE_URL } from "../../config/axios";

const CF_BASE = `${BASE_URL}/siteproject`;

// Accepts: [..], {data:[..]}, {results:[..]}, {fields:[..]}, {custom_fields:[..]}
const unwrap = (d) => {
    if (Array.isArray(d)) return d;
    return d?.data ?? d?.results ?? d?.fields ?? d?.custom_fields ?? [];
};

export const fetchCustomFieldDefs = async () => {
    try {
        const r = await axios.get(`${CF_BASE}/custom-fields/`);
        return unwrap(r.data).filter((f) => f.is_active !== false);
    } catch (e) {
        console.error("[custom-fields] defs failed:", e?.response?.status, e?.config?.url, e?.response?.data);
        return [];
    }
};

export const fetchTenderCustomFields = async (tenderId) => {
    try {
        const r = await axios.get(`${CF_BASE}/tender/custom-fields/${tenderId}/`);
        return unwrap(r.data);
    } catch (e) {
        console.error("[custom-fields] tender failed:", e?.response?.status, e?.config?.url, e?.response?.data);
        return [];
    }
};

export const saveTenderCustomFields = (tenderId, values) =>
    axios.put(`${CF_BASE}/tender/custom-fields/${tenderId}/`, values);

export const fieldAppliesTo = (f, sector) => {
    const list = f.applies_to_sectors;
    if (!list?.length) return true;               // applies to all sectors
    if (!sector) return false;
    return list.some((s) => String(s).toLowerCase() === String(sector).toLowerCase());
};
export const formatCustomValue = (f) => {
    if (f.value === null || f.value === undefined || f.value === "") return "—";
    if (f.field_type === "boolean") return f.value ? "Yes" : "No";
    return String(f.value);
};

const CustomFieldInput = ({ field, value, onChange, error }) => {
    const cls = `form-input input-form ${error ? "error" : ""}`;
    const v = value ?? "";
    const set = (val) => onChange(field.key, val);
    switch (field.field_type) {
        case "boolean":
            return (
                <select className={cls} value={value === true ? "true" : value === false ? "false" : ""}
                    onChange={(e) => set(e.target.value === "" ? null : e.target.value === "true")}>
                    <option value="">Select</option><option value="true">Yes</option><option value="false">No</option>
                </select>
            );
        case "dropdown":
            return (
                <select className={cls} value={v} onChange={(e) => set(e.target.value || null)}>
                    <option value="">Select {field.name}</option>
                    {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        case "integer":
        case "decimal":
            return (
                <input type="number" className={cls} value={v} onWheel={(e) => e.target.blur()}
                    step={field.field_type === "integer" ? 1 : "any"}
                    onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))} />
            );
        case "date":
            return <input type="date" className={cls} value={v} onChange={(e) => set(e.target.value || null)} />;
        default:
            return <input type="text" className={cls} value={v} onChange={(e) => set(e.target.value || null)} />;
    }
};

export const CustomFieldsForm = ({ fields, values, errors = {}, onChange, className = "flex-columns" }) => (
    <>
        {fields.map((f) => (
            <div className={className} key={f.key}>
                <label className="form-labels">
                    {f.name}{f.is_required && <span className="required">*</span>}
                </label>
                <CustomFieldInput field={f} value={values[f.key]} onChange={onChange} error={errors[f.key]} />
                {errors[f.key] && <span className="error-message">{[].concat(errors[f.key]).join(" ")}</span>}
            </div>
        ))}
    </>
);

export const createCustomField = (payload) =>
    axios.post(`${CF_BASE}/custom-fields/`, payload, {
        params: { emp_code: sessionStorage.getItem("sitemanagement_emp_code") },
    });

const SECTORS = ["Highway", "Railway", "Metro", "Water", "Ropeway", "Building", "Other"];
const FIELD_TYPES = [
    { value: "text", label: "Text" },
    { value: "integer", label: "Integer" },
    { value: "decimal", label: "Decimal" },
    { value: "boolean", label: "Yes / No" },
    { value: "dropdown", label: "Dropdown" },
    { value: "date", label: "Date" },
];
const slugify = (s) =>
    s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const flattenErrors = (data) =>
    typeof data === "string"
        ? data
        : Object.entries(data || {})
            .map(([k, v]) => `${k}: ${[].concat(v).join(" ")}`)
            .join(" | ");

export const CreateCustomFieldInline = ({ defaultSector, onCreated }) => {
    const empty = {
        name: "",
        field_type: "text",
        optionsText: "",
        is_required: false,
        sectors: [],
    };

    const [open, setOpen] = useState(false);
    const [f, setF] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const openPanel = () => {
        setF({
            ...empty,
            sectors: defaultSector ? [defaultSector] : [],
        });
        setErr("");
        setOpen(true);
    };

    const toggleSector = (s) => {
        setF((p) => ({
            ...p,
            sectors: p.sectors.includes(s)
                ? p.sectors.filter((x) => x !== s)
                : [...p.sectors, s],
        }));
    };

    const save = async () => {
        const name = f.name.trim();

        if (!name) {
            return setErr("Field name is required.");
        }

        const options = f.optionsText
            .split(",")
            .map((o) => o.trim())
            .filter(Boolean);

        if (f.field_type === "dropdown" && options.length < 2) {
            return setErr("Add at least 2 comma-separated options.");
        }

        setSaving(true);
        setErr("");

        try {
            const res = await createCustomField({
                key: slugify(name),
                name,
                field_type: f.field_type,
                options: f.field_type === "dropdown" ? options : [],
                is_required: f.is_required,
                applies_to_sectors: f.sectors,
            });

            const created = res.data?.data ?? res.data;

            setOpen(false);
            onCreated?.(created);
        } catch (e) {
            setErr(
                flattenErrors(e?.response?.data) ||
                "Could not create field."
            );
        } finally {
            setSaving(false);
        }
    };

    // Enter inside inputs must not submit parent form
    const stopEnter = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
        }
    };

    if (!open) {
        return (
            <button
                type="button"
                className="cf-create-btn"
                onClick={openPanel}
            >
                <span className="cf-create-btn-icon">+</span>
                Create Custom Field
            </button>
        );
    }

    return (
        <div className="cf-panel">

            {/* Header */}
            <div className="cf-panel-header">
                <div>
                    <div className="cf-panel-title">
                        Create Custom Field
                    </div>

                    <div className="cf-panel-subtitle">
                        Add a custom field and configure where it should appear.
                    </div>
                </div>

                <button
                    type="button"
                    className="cf-close-btn"
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                >
                    ×
                </button>
            </div>

            {/* Form */}
            <div className="cf-form">

                {/* Field Name */}
                <div className="cf-form-group">
                    <label className="cf-label">
                        Field Name
                        <span className="cf-required">*</span>
                    </label>

                    <input
                        className="cf-input"
                        value={f.name}
                        onKeyDown={stopEnter}
                        placeholder="e.g. Fixed Cost"
                        onChange={(e) =>
                            setF({
                                ...f,
                                name: e.target.value,
                            })
                        }
                    />
                </div>

                {/* Field Type */}
                <div className="cf-form-group">
                    <label className="cf-label">
                        Field Type
                    </label>

                    <select
                        className="cf-input cf-select"
                        value={f.field_type}
                        onChange={(e) =>
                            setF({
                                ...f,
                                field_type: e.target.value,
                            })
                        }
                    >
                        {FIELD_TYPES.map((t) => (
                            <option
                                key={t.value}
                                value={t.value}
                            >
                                {t.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Dropdown Options */}
                {f.field_type === "dropdown" && (
                    <div className="cf-form-group">
                        <label className="cf-label">
                            Options
                            <span className="cf-label-hint">
                                Comma separated
                            </span>
                        </label>

                        <input
                            className="cf-input"
                            value={f.optionsText}
                            onKeyDown={stopEnter}
                            placeholder="Lump Sum, Item Rate, Percentage"
                            onChange={(e) =>
                                setF({
                                    ...f,
                                    optionsText: e.target.value,
                                })
                            }
                        />

                        <div className="cf-help-text">
                            Add at least two options separated by commas.
                        </div>
                    </div>
                )}

                {/* Sectors */}
                <div className="cf-form-group">
                    <div className="cf-label-row">
                        <label className="cf-label">
                            Applies to Sectors
                        </label>

                        <span className="cf-label-hint">
                            None selected = all sectors
                        </span>
                    </div>

                    <div className="cf-sector-grid">
                        {SECTORS.map((s) => (
                            <label
                                key={s}
                                className={`cf-sector-option ${f.sectors.includes(s)
                                    ? "cf-sector-option--active"
                                    : ""
                                    }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={f.sectors.includes(s)}
                                    onChange={() => toggleSector(s)}
                                />

                                <span className="cf-sector-check">
                                    {f.sectors.includes(s) ? "✓" : ""}
                                </span>

                                <span>{s}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Required */}
                <label
                    className={`cf-required-option ${f.is_required
                        ? "cf-required-option--active"
                        : ""
                        }`}
                >
                    <input
                        type="checkbox"
                        checked={f.is_required}
                        onChange={(e) =>
                            setF({
                                ...f,
                                is_required: e.target.checked,
                            })
                        }
                    />

                    <span className="cf-required-check">
                        {f.is_required ? "✓" : ""}
                    </span>

                    <span>
                        <strong>Required field</strong>
                        <small>
                            Users must provide a value
                        </small>
                    </span>
                </label>

                {/* Error */}
                {err && (
                    <div className="cf-error">
                        <span className="cf-error-icon">!</span>
                        {err}
                    </div>
                )}

            </div>

            {/* Footer */}
            <div className="cf-footer">
                <button
                    type="button"
                    className="cf-btn cf-btn--cancel"
                    onClick={() => setOpen(false)}
                    disabled={saving}
                >
                    Cancel
                </button>

                <button
                    type="button"
                    className="cf-btn cf-btn--save"
                    disabled={saving}
                    onClick={save}
                >
                    {saving ? (
                        <>
                            <span className="cf-spinner" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <span>✓</span>
                            Save Field
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};